import React, { useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';

function CaseStudyPage() {
  const { projectId } = useParams();

  const caseStudies = {
    'e-commerce-platform': {
      title: 'E-Commerce Platform',
      subtitle: 'Revolutionizing Online Retail',
      heroImage: 'https://images.unsplash.com/photo-1557821552-17105176677c?w=1200&h=800&fit=crop',
      client: 'RetailMax Inc.',
      industry: 'E-Commerce / Retail',
      challenge: `RetailMax Inc. was struggling with an outdated e-commerce platform that couldn't handle their growing customer base. They faced issues with slow page loads, poor mobile experience, and lack of personalization features. Their conversion rate was declining, and customers were abandoning carts at an alarming rate of 78%.`,
      solution: `We developed a completely new e-commerce platform using React for the frontend and Node.js for the backend. Key implementations included:
      
• AI-powered recommendation engine using machine learning algorithms
• Real-time inventory management system with predictive restocking
• Progressive Web App (PWA) for seamless mobile experience
• Advanced caching strategies reducing load times by 70%
• Personalized user journeys based on browsing behavior
• Streamlined checkout process with multiple payment options`,
      process: [
        {
          phase: 'Discovery & Research',
          duration: '2 Weeks',
          description: 'Conducted user research, analyzed competitor platforms, and identified key pain points in the existing system.'
        },
        {
          phase: 'UX/UI Design',
          duration: '4 Weeks',
          description: 'Created wireframes, prototypes, and final designs with focus on conversion optimization and user experience.'
        },
        {
          phase: 'Development',
          duration: '16 Weeks',
          description: 'Built the platform using agile methodology with 2-week sprints, continuous integration, and regular client feedback.'
        },
        {
          phase: 'Testing & QA',
          duration: '3 Weeks',
          description: 'Comprehensive testing including performance, security, accessibility, and cross-browser compatibility.'
        },
        {
          phase: 'Launch & Optimization',
          duration: '2 Weeks',
          description: 'Phased rollout, A/B testing, and continuous optimization based on real user data.'
        }
      ],
      results: [
        { metric: '300%', label: 'Increase in Sales', icon: 'fas fa-chart-line' },
        { metric: '50%', label: 'Reduced Cart Abandonment', icon: 'fas fa-shopping-cart' },
        { metric: '70%', label: 'Faster Page Load', icon: 'fas fa-tachometer-alt' },
        { metric: '2M+', label: 'Monthly Visitors', icon: 'fas fa-users' }
      ],
      technologies: ['React', 'Node.js', 'MongoDB', 'AWS', 'Stripe', 'Redis', 'Elasticsearch', 'Docker'],
      testimonial: {
        text: "NestedInfo transformed our online presence completely. The new platform exceeded all our expectations and has been a game-changer for our business. Sales have tripled and customer satisfaction is at an all-time high.",
        author: "John Smith",
        role: "CEO, RetailMax Inc.",
        image: "https://images.unsplash.com/photo-1560250097-0b93528c311a?w=100&h=100&fit=crop&crop=face"
      }
    },
    'finance-mobile-app': {
      title: 'Finance Mobile App',
      subtitle: 'Secure Banking in Your Pocket',
      heroImage: 'https://images.unsplash.com/photo-1551288049-bebda4e38f71?w=1200&h=800&fit=crop',
      client: 'FinSecure Bank',
      industry: 'Banking / FinTech',
      challenge: `FinSecure Bank needed a mobile banking solution that would compete with modern fintech apps while maintaining the security standards required for financial institutions. Their existing mobile app had poor user ratings, limited functionality, and security concerns.`,
      solution: `We built a comprehensive mobile banking app using React Native for cross-platform compatibility. Security was our top priority, implementing multiple layers of protection.`,
      process: [
        {
          phase: 'Security Audit & Planning',
          duration: '3 Weeks',
          description: 'Conducted thorough security analysis and created a robust security architecture.'
        },
        {
          phase: 'UX Research',
          duration: '3 Weeks',
          description: 'Interviewed users, analyzed banking app trends, and designed intuitive user flows.'
        },
        {
          phase: 'Development',
          duration: '20 Weeks',
          description: 'Developed the app with security-first approach, regular penetration testing, and compliance checks.'
        },
        {
          phase: 'Security Testing',
          duration: '4 Weeks',
          description: 'Third-party security audit, vulnerability assessment, and compliance certification.'
        },
        {
          phase: 'Beta Launch',
          duration: '4 Weeks',
          description: 'Soft launch with select users, feedback collection, and refinement.'
        }
      ],
      results: [
        { metric: '500K+', label: 'App Downloads', icon: 'fas fa-download' },
        { metric: '4.8★', label: 'App Store Rating', icon: 'fas fa-star' },
        { metric: '60%', label: 'Increased Engagement', icon: 'fas fa-chart-bar' },
        { metric: '0', label: 'Security Breaches', icon: 'fas fa-shield-alt' }
      ],
      technologies: ['React Native', 'TypeScript', 'Firebase', 'Plaid API', 'Face ID', 'Touch ID', 'AWS', 'GraphQL'],
      testimonial: {
        text: "The app has revolutionized how our customers manage their finances. Security and UX are top-notch. We've seen tremendous growth in mobile banking adoption.",
        author: "Sarah Johnson",
        role: "CTO, FinSecure Bank",
        image: "https://images.unsplash.com/photo-1573496359142-b8d87734a5a2?w=100&h=100&fit=crop&crop=face"
      }
    }
  };

  // Default case study for other projects
  const defaultCaseStudy = {
    title: 'Project Case Study',
    subtitle: 'Detailed Analysis',
    heroImage: 'https://images.unsplash.com/photo-1460925895917-afdab827c52f?w=1200&h=800&fit=crop',
    client: 'Valued Client',
    industry: 'Technology',
    challenge: 'Our client faced significant challenges in their digital transformation journey that required innovative solutions.',
    solution: 'We implemented a comprehensive solution using cutting-edge technologies and best practices.',
    process: [
      { phase: 'Discovery', duration: '2 Weeks', description: 'Understanding requirements and planning.' },
      { phase: 'Design', duration: '3 Weeks', description: 'Creating user-centered designs.' },
      { phase: 'Development', duration: '12 Weeks', description: 'Building the solution with agile methodology.' },
      { phase: 'Testing', duration: '2 Weeks', description: 'Quality assurance and optimization.' },
      { phase: 'Launch', duration: '1 Week', description: 'Deployment and support.' }
    ],
    results: [
      { metric: '200%', label: 'Performance Improvement', icon: 'fas fa-rocket' },
      { metric: '50%', label: 'Cost Reduction', icon: 'fas fa-dollar-sign' },
      { metric: '95%', label: 'Client Satisfaction', icon: 'fas fa-smile' },
      { metric: '24/7', label: 'Support', icon: 'fas fa-headset' }
    ],
    technologies: ['React', 'Node.js', 'Cloud Services', 'AI/ML'],
    testimonial: {
      text: "Working with NestedInfo was a fantastic experience. They delivered beyond our expectations.",
      author: "Happy Client",
      role: "Project Stakeholder",
      image: "https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?w=100&h=100&fit=crop&crop=face"
    }
  };

  const caseStudy = caseStudies[projectId] || defaultCaseStudy;

  useEffect(() => {
    window.scrollTo({ top: 0, behavior: 'smooth' });
  }, [projectId]);

  return (
    <div className="page-transition pt-20">
      {/* Hero Section */}
      <section className="relative h-[70vh] min-h-[600px]">
        <img 
          src={caseStudy.heroImage} 
          alt={caseStudy.title}
          className="w-full h-full object-cover"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-black/50 to-transparent"></div>
        <div className="absolute bottom-0 left-0 right-0 p-8 md:p-16">
          <div className="max-w-7xl mx-auto">
            <div className="inline-block bg-green-500 text-white px-4 py-2 rounded-full text-sm font-semibold mb-4">
              Case Study
            </div>
            <h1 className="text-4xl md:text-6xl font-black text-white mb-4">{caseStudy.title}</h1>
            <p className="text-2xl text-green-300 font-semibold mb-6">{caseStudy.subtitle}</p>
            <div className="flex flex-wrap gap-6 text-gray-300">
              <div className="flex items-center gap-2">
                <i className="fas fa-building"></i>
                <span>{caseStudy.client}</span>
              </div>
              <div className="flex items-center gap-2">
                <i className="fas fa-industry"></i>
                <span>{caseStudy.industry}</span>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Challenge Section */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-red-100 rounded-2xl flex items-center justify-center text-red-600 text-2xl">
              <i className="fas fa-exclamation-triangle"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">The Challenge</h2>
          </div>
          <p className="text-xl text-gray-600 leading-relaxed whitespace-pre-line">
            {caseStudy.challenge}
          </p>
        </div>
      </section>

      {/* Solution Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-4 mb-8">
            <div className="w-16 h-16 bg-green-100 rounded-2xl flex items-center justify-center text-green-600 text-2xl">
              <i className="fas fa-lightbulb"></i>
            </div>
            <h2 className="text-3xl md:text-4xl font-black text-gray-900">Our Solution</h2>
          </div>
          <p className="text-xl text-gray-600 leading-relaxed whitespace-pre-line">
            {caseStudy.solution}
          </p>
        </div>
      </section>

      {/* Process Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Our Process</h2>
            <p className="text-xl text-gray-600">How we delivered the project</p>
          </div>
          <div className="relative">
            {/* Timeline Line */}
            <div className="hidden md:block absolute left-1/2 transform -translate-x-1/2 w-1 h-full bg-green-200"></div>
            
            {caseStudy.process.map((phase, index) => (
              <div 
                key={index} 
                className={`relative flex items-center mb-12 ${
                  index % 2 === 0 ? 'md:flex-row' : 'md:flex-row-reverse'
                }`}
              >
                <div className={`w-full md:w-1/2 ${index % 2 === 0 ? 'md:pr-12 md:text-right' : 'md:pl-12'}`}>
                  <div className="bg-white p-6 rounded-2xl shadow-lg border border-gray-100">
                    <div className="text-green-600 font-semibold mb-2">{phase.duration}</div>
                    <h3 className="text-xl font-bold text-gray-900 mb-2">{phase.phase}</h3>
                    <p className="text-gray-600">{phase.description}</p>
                  </div>
                </div>
                <div className="hidden md:flex absolute left-1/2 transform -translate-x-1/2 w-12 h-12 bg-green-500 rounded-full items-center justify-center text-white font-bold">
                  {index + 1}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-20 bg-gradient-to-br from-green-500 to-blue-600">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-black text-white mb-4">Results & Impact</h2>
            <p className="text-xl text-green-100">The numbers speak for themselves</p>
          </div>
          <div className="grid md:grid-cols-4 gap-8">
            {caseStudy.results.map((result, index) => (
              <div key={index} className="text-center p-8 bg-white/10 backdrop-blur-sm rounded-2xl">
                <div className="text-4xl text-white mb-4">
                  <i className={result.icon}></i>
                </div>
                <div className="text-4xl md:text-5xl font-black text-white mb-2">{result.metric}</div>
                <div className="text-green-100 font-medium">{result.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Technologies Section */}
      <section className="py-20 bg-white">
        <div className="max-w-6xl mx-auto px-6">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-black text-gray-900 mb-4">Technologies Used</h2>
          </div>
          <div className="flex flex-wrap justify-center gap-4">
            {caseStudy.technologies.map((tech, index) => (
              <span 
                key={index}
                className="bg-gray-100 text-gray-800 px-6 py-3 rounded-full font-semibold text-lg hover:bg-green-500 hover:text-white transition-all cursor-default"
              >
                {tech}
              </span>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonial Section */}
      <section className="py-20 bg-gray-900">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center">
            <div className="text-6xl text-green-500 mb-8">
              <i className="fas fa-quote-left"></i>
            </div>
            <p className="text-2xl md:text-3xl text-white font-medium leading-relaxed mb-10">
              "{caseStudy.testimonial.text}"
            </p>
            <div className="flex items-center justify-center gap-4">
              <img 
                src={caseStudy.testimonial.image} 
                alt={caseStudy.testimonial.author}
                className="w-16 h-16 rounded-full object-cover"
              />
              <div className="text-left">
                <div className="font-bold text-white text-lg">{caseStudy.testimonial.author}</div>
                <div className="text-gray-400">{caseStudy.testimonial.role}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Navigation */}
      <section className="py-16 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link 
              to={`/project/${projectId}`}
              className="bg-gray-200 text-gray-800 px-8 py-4 rounded-full font-semibold hover:bg-gray-300 transition-all flex items-center justify-center gap-2"
            >
              <i className="fas fa-arrow-left"></i>
              Back to Project
            </Link>
            <Link 
              to="/portfolio"
              className="bg-green-500 text-white px-8 py-4 rounded-full font-semibold hover:bg-green-600 transition-all flex items-center justify-center gap-2"
            >
              View All Projects
              <i className="fas fa-th-large"></i>
            </Link>
            <Link 
              to="/contact"
              className="bg-blue-600 text-white px-8 py-4 rounded-full font-semibold hover:bg-blue-700 transition-all flex items-center justify-center gap-2"
            >
              Start Your Project
              <i className="fas fa-arrow-right"></i>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}

export default CaseStudyPage;